import React, { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../reducers'
import { phaseEnum } from '../../../enums/phaseEnum'
import { PRL_AUTO, propertyType, requestType } from '../../../enums/contractsEnum'
import { ContractsModel } from '../../../types/contracts/contractsModel'
import { Grid, makeStyles } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import Link from '../../../components/link/Link'
import { WHITE } from '../../../theme'
import styles from '../../../styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { ResponsiveContext } from '../../../utils/context/responsiveContext'
import classnames from 'classnames'
import { saveInteractions } from '../../gtm/utils/gtmCallback'
import { displayModal } from '../../reducers/modal/actions'
import { modalTypes } from '../../../enums/modalTypeEnum'
import { subCategoryEnum } from '../../../enums/makeRequestEnum'
import { RequestItemModel } from '../../requests/requestsModel'
import portals from '../../../enums/portalsEnum'
import { ASSURANCE_CONCERN } from '../../requests/makeRequest/stepper/Stepper'
import { CONTRACT } from '../../requests/makeRequest/stepper/step2/RequestPurpose'
import { MakeRequestContext } from '../../requests/makeRequest/MakeRequestContext'
import { canDisplayAssuranceAlert, isNouveauLocataire } from '../../../components/topAlert/utils/assuranceUtils'
import EDLModal from '../edl/components/EDLModal'
import { ASSURANCE_CATEGORY } from '../../requests/list/RequestCard'
import Loader from '../../../components/loader/Loader'
import { EDLContext } from '../edl/context/EDLContext'
import Button from '../../../components/button/Button'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean, isAssuranceDeposee: boolean }>(
	(theme) => ({
		...styles(theme),
		preparezArriveeContainer: {
			margin: 0,
			marginBottom: 15
		},
		titleContainer: {
			...styles(theme).title3,
			margin: 0,
			marginBottom: props => props.isLargeScreen ? 18 : 22,
			fontSize: props => props.isLargeScreen ? 28 : 23,
			lineHeight: props => props.isLargeScreen ? '35px' : '29px'
		},
		blocContainer: {
			background: WHITE,
			alignContent: 'flex-start',
			padding: props => props.isLargeScreen ? '19px 55px 18px 21px' : '17px 61px 18px 21px',
			maxWidth: props => props.isLargeScreen ? '48%' : '100%',

			'& h2': {
				...styles(theme).title4,
				fontSize: props => props.isLargeScreen ? 16 : 13,
				lineHeight: props => props.isLargeScreen ? '24px' : '16px',
				color: theme.palette.blue.main,
				margin: 0
			},
			'& p': {
				...styles(theme).text17,
				color: theme.palette.blue.main,
				fontSize: props => props.isLargeScreen ? 13 : 12,
				lineHeight: props => props.isLargeScreen ? '20px' : '19px',
				margin: 0,
				marginTop: 20
			}
		},
		marginBloc: {
			marginBottom: 25
		},
		linkStyle: {
			marginTop: props => props.isLargeScreen ? 40 : 34,
			paddingLeft: 0
		},
		marginAssurance: {
			marginBottom: 'auto'
		},
		textGris: {
			'& h2, p': { color: 'grey !important' }
		}
	})
)

const PreparezArrivee = () => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { idVisiteEdle, shouldDisplayEdle, isRdvEdlePlanned, loading, offre, refresh, bail, shouldDisplayEdls, dateEffetBail } = useContext(EDLContext)
	const dispatch = useDispatch()
	const user = useSelector((state: RootState) => state.user)
	const contracts = useSelector((state: RootState) => state.contracts)
	const requests: RequestItemModel[] = useSelector((state: RootState) => state.requests)
	const intl = useIntl()
	const { request } = useContext(MakeRequestContext)
	const contratsEnExploitation = (contracts as ContractsModel[]).filter((contract) => contract.phase === phaseEnum.exploitation)
		.sort((a, b) => {
			if (b.dateEffet && a.dateEffet) {
				return b.dateEffet.getTime() - a.dateEffet.getTime()
			}
			return 0
		})

	const isAssuranceDeposee = requests.filter(request => request.category === ASSURANCE_CATEGORY).length > 0

	const classes = useStyles({ isLargeScreen, isAssuranceDeposee })

	// Bloc afficher si le client est sur Résidence, possède au moins un contrat en exploitation avec un appartement & que le numéro de PDL contient une valeur
	const afficherPDL = () => process.env.REACT_APP_TARGET === portals.residence && contratsEnExploitation.filter((contract) => {
		return contract.type === propertyType.apartment && (contract.dateEffet && new Date(contract.dateEffet) >= new Date())
	}).length > 0 && contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0].numeroDePdl

	//Bloc afficher si le client possède au moins un contrat en exploitation et s'il n'a pas de mode de paiement en prélèvement auto
	const afficherPrelAuto = () => contratsEnExploitation.filter(contract => contract.typeDePaiement !== PRL_AUTO).length > 0 && !(requests.filter(request => request.category === subCategoryEnum.bill.debit && !request.cloture).length > 0) && contratsEnExploitation.filter((contract) => {
		let date = contract.dateEffet && new Date(contract.dateEffet)
		return date && new Date(date.setMonth(date.getMonth() + 1)) >= new Date()
	})

	//Bloc afficher si le client possède au moins un contrat non parking en exploitation
	const afficherFinAssurance = () => canDisplayAssuranceAlert(requests) && contratsEnExploitation.filter(contract => {
		return contract.type === propertyType.apartment && (!contract.dateFinAssurance || (contract.dateFinAssurance && new Date(contract.dateFinAssurance) <= new Date(new Date().setMonth((new Date()).getMonth() + 1))))
		&& (contract.dateEffet && new Date(contract.dateEffet) >= new Date())
	}).length > 0

	const afficherBloc = () => {
		return isNouveauLocataire(contracts)
	}

	if (!afficherBloc() || shouldDisplayEdls) {
		return <></>
	}

	if (!user) {
		return <></>
	}

	if (loading) {
		return <Loader></Loader>
	}

	return (
		<Grid container item xs={12} justify="space-between" className={classes.preparezArriveeContainer}>
			<Grid container item xs={12}>
				<h1 className={classes.titleContainer}>
					<FormattedMessage
						id="preparezArrivee.titre"
						defaultMessage="Préparez votre arrivée"
						description="Titre du bloc Préparez votre arrivée"
					/>
				</h1>
			</Grid>
			{afficherFinAssurance() && (
					<Grid container item xs={12} md={6} className={
						classnames(
							classes.blocContainer,
							isLargeScreen ? classes.marginAssurance : classes.marginBloc
						)
					}>
						<Grid item xs={12}>
							<h2>
								<FormattedMessage
									id="preparezArrivee.finAssurance.titre"
									defaultMessage="Partagez votre attestation d’assurance"
									description="Titre du bloc de Partage d'assurance"
								/>
							</h2>
						</Grid>

						<Grid item xs={12}>
							<p>
								<FormattedMessage
									id="preparezArrivee.finAssurance.texte"
									defaultMessage="Veuillez nous transmettre votre attestation d’assurance. Elle est obligatoire pour l’entrée dans les lieux"
									description="Titre du bloc de Partage d'assurance"
								/>
							</p>
						</Grid>

						<Grid item xs={12}>
							<Link
								color="colorAccessibility"
								textStyle="text4"
								ariaLabel={
									intl.formatMessage({
										id: 'preparezArrivee.finAssurance.lien',
										defaultMessage: 'Transmettre l\'attestation d\'assurance',
										description: 'Titre du bloc de Partage d\'assurance'
									})
								}
								className={classes.linkStyle}
								onClick={() => {
									saveInteractions(
										{
											category: 'Mes Services',
											action: 'Souscrire en ligne',
											label: 'Mes Services::Souscrire en ligne::call to action'
										}
									)

									request.requestConcern = ASSURANCE_CONCERN
									request.requestPurpose = CONTRACT
									request.requestType = requestType.administrative
									dispatch(displayModal(modalTypes.demandes))
								}}
							>
								<FormattedMessage
									id="preparezArrivee.finAssurance.lien"
									defaultMessage="Transmettre l'attestation d'assurance"
									description="Titre du bloc de Partage d'assurance"
								/>
							</Link>
						</Grid>
					</Grid>
				)
			}
			{shouldDisplayEdle && (
					<Grid container item xs={12} md={6} className={
						classnames(
							classes.blocContainer,
							classes.marginBloc,
							{
								[classes.textGris]: !isAssuranceDeposee
							}
						)
					}>
						<Grid item xs={12}>
							<h2>
								<FormattedMessage
									id="preparezArrivee.etatLieuxRemiseCles.titre"
									defaultMessage="Etat des lieux et remise des clés"
									description="Titre du bloc de Etat des lieux et remise des clés"
								/>
							</h2>
						</Grid>

						<Grid item xs={12}>
							<p>
								<FormattedMessage
									id={`preparezArrivee.etatLieuxRemiseCles.${isRdvEdlePlanned ? (bail?.jour1 ? 'texteRdvPlanifieCreneaux' :'texteRdvPlanifie') : 'texteRdvNonPlanifie'}`}
									defaultMessage="Votre YouFirst Manager fera l’état des lieux avec vous avant la remise des clés. Pensez à le contacter pour prendre le rendez-vous."
									description="Texte du bloc de Etat des lieux et remise des clés"
								/>
							</p>
						</Grid>
						<Grid item xs={12}>
							<Button
								onClick={() => dispatch(displayModal(modalTypes.edle))}
								color="colorAccessibility"
								textStyle="text4"
								disabled={!isAssuranceDeposee}
								buttonStyle={classes.linkStyle}
								size="smallButton"
								background="backgroundInherit"
								border="borderWhite"
								ariaLabel={
									intl.formatMessage({
										id: `preparezArrivee.etatLieuxRemiseCles.${isRdvEdlePlanned ? 'ctaRdvPlanifie' : 'ctaRdvNonPlanifie'}`
									})
								}
							>

								<FormattedMessage
									id={`preparezArrivee.etatLieuxRemiseCles.${isRdvEdlePlanned ? 'ctaRdvPlanifie' : 'ctaRdvNonPlanifie'}`}
								/>
								<ChevronRightIcon />
							</Button>
						</Grid>
					</Grid>
			)}
			{afficherPDL() && (
				<Grid
					container
					item
					xs={12}
					md={6}
					className={
						classnames(
							classes.blocContainer,
							classes.marginBloc
						)
					}
				>
					<Grid item xs={12}>
						<h2>
							<FormattedMessage
								id="preparezArrivee.pdl.titre"
								defaultMessage="Point de livraison énergie (PDL)"
									description="Titre du bloc Point De Livraison"
								/>
							</h2>
						</Grid>

						<Grid item xs={12}>
							<p>
								<FormattedMessage
									id="preparezArrivee.pdl.texte"
									defaultMessage="Communiquez le N°{numeroPDL} à vos fournisseurs d'énergie pour l'ouverture de votre compte."
									description="Texte du bloc PDL"
									values={{
										numeroPDL: contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0].numeroDePdl
									}}
								/>
							</p>
						</Grid>
					</Grid>
				)
			}

			{
				afficherPrelAuto() && (
					<Grid container item xs={12} md={6} className={
						classnames(
							classes.blocContainer,
							classes.marginBloc
						)
					}>
						<Grid item xs={12}>
							<h2>
								<FormattedMessage
									id="preparezArrivee.prelAuto.titre"
									defaultMessage="Prélèvement automatique"
									description="Titre du bloc de Prélèvement automatique"
								/>
							</h2>
						</Grid>

						<Grid item xs={12}>
							<p>
								<FormattedMessage
									id="preparezArrivee.prelAuto.texte"
									defaultMessage="Adoptez-le et simplifiez-vous la gestion de votre location."
									description="Texte du bloc de Prélèvement automatique"
								/>
							</p>
						</Grid>

						<Grid item xs={12}>
							<Link
								color="colorAccessibility"
								textStyle="text4"
								ariaLabel={
									intl.formatMessage({
										id: 'preparezArrivee.prelAuto.lien',
										defaultMessage: 'Souscrire',
										description: 'Lien du bloc de Prélèvement automatique'
									})
								}
								className={classes.linkStyle}
								onClick={() => {
									saveInteractions(
										{
											category: 'Mes Services',
											action: 'Souscrire en ligne',
											label: 'Mes Services::Souscrire en ligne::call to action'
										}
									)

									dispatch(displayModal(modalTypes.prelevement))
								}}
							>
								<FormattedMessage
									id="preparezArrivee.prelAuto.lien"
									defaultMessage="Souscrire"
									description="Lien du bloc de Prélèvement automatique"
								/>
							</Link>
						</Grid>
					</Grid>
				)
			}
			{!!offre && bail?.id &&
				<EDLModal
					offre={offre}
					onValidate={() => refresh()}
					bail={bail}
					idVisiteEdl={idVisiteEdle}
					isEdle={true}
					dateEffetBail={dateEffetBail}
					dateCongesSouhaitee={null}
				/>
			}
		</Grid>
	)
}

export default PreparezArrivee
